import { apiRequest } from './client';

/** Team collaboration (SRS #70–#73): shared folders, notes, comments. */

const BASE = '/api/v1/enterprise/collab';
const TOKEN_KEY = 'policyora_token';

function opts(extra: RequestInit = {}): RequestInit {
  const token = localStorage.getItem(TOKEN_KEY);
  return { ...extra, headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(extra.headers || {}) } };
}

export type TargetType = 'regulation' | 'document' | 'folder';
export interface Person { id: number | null; name: string | null; email: string | null }

export interface TeamNote {
  id: number; target_type: TargetType; target_ref: string; body: string; is_private: boolean;
  author: Person; created_at: string; updated_at: string;
}
export interface TeamComment {
  id: number; target_type: TargetType; target_ref: string; parent_id: number | null; body: string;
  author: Person; created_at: string; updated_at: string; resolved_at: string | null; resolved_by: Person | null;
}
export interface TeamFolder {
  id: number; parent_id: number | null; name: string; description: string | null;
  created_by: Person; created_at: string; updated_at: string; item_count: number | null;
}
export interface FolderItem {
  id: number; item_type: 'regulation' | 'document'; item_ref: string; title: string | null; added_by: Person; added_at: string;
}
export interface ActivityItem {
  kind: 'note' | 'comment' | 'folder_item'; at: string; author: Person; target_type: string; target_ref: string;
  preview: string; id: number; folder_id?: number;
}

export const listNotes = (target_type: TargetType, target_ref: string) =>
  apiRequest<{ notes: TeamNote[]; me: number; is_owner: boolean }>(`${BASE}/notes?target_type=${target_type}&target_ref=${encodeURIComponent(target_ref)}`, opts());
export const createNote = (body: { target_type: TargetType; target_ref: string; body: string; is_private?: boolean }) =>
  apiRequest<TeamNote>(`${BASE}/notes`, opts({ method: 'POST', body: JSON.stringify(body) }));
export const updateNote = (id: number, body: { body?: string; is_private?: boolean }) =>
  apiRequest<TeamNote>(`${BASE}/notes/${id}`, opts({ method: 'PATCH', body: JSON.stringify(body) }));
export const deleteNote = (id: number) => apiRequest<void>(`${BASE}/notes/${id}`, opts({ method: 'DELETE' }));

export const listComments = (target_type: TargetType, target_ref: string) =>
  apiRequest<{ comments: TeamComment[]; me: number; is_owner: boolean }>(`${BASE}/comments?target_type=${target_type}&target_ref=${encodeURIComponent(target_ref)}`, opts());
export const createComment = (body: { target_type: TargetType; target_ref: string; body: string; parent_id?: number | null }) =>
  apiRequest<TeamComment>(`${BASE}/comments`, opts({ method: 'POST', body: JSON.stringify(body) }));
export const updateComment = (id: number, body: { body?: string; resolved?: boolean }) =>
  apiRequest<TeamComment>(`${BASE}/comments/${id}`, opts({ method: 'PATCH', body: JSON.stringify(body) }));
export const deleteComment = (id: number) => apiRequest<void>(`${BASE}/comments/${id}`, opts({ method: 'DELETE' }));

export const listFolders = () => apiRequest<{ folders: TeamFolder[]; is_owner: boolean }>(`${BASE}/folders`, opts());
export const createFolder = (body: { name: string; parent_id?: number | null; description?: string }) =>
  apiRequest<TeamFolder>(`${BASE}/folders`, opts({ method: 'POST', body: JSON.stringify(body) }));
export const updateFolder = (id: number, body: { name?: string; description?: string }) =>
  apiRequest<TeamFolder>(`${BASE}/folders/${id}`, opts({ method: 'PATCH', body: JSON.stringify(body) }));
export const deleteFolder = (id: number) => apiRequest<void>(`${BASE}/folders/${id}`, opts({ method: 'DELETE' }));
export const listFolderItems = (id: number) => apiRequest<{ items: FolderItem[] }>(`${BASE}/folders/${id}/items`, opts());
export const addFolderItem = (id: number, body: { item_type: 'regulation' | 'document'; item_ref: string; title?: string }) =>
  apiRequest<FolderItem>(`${BASE}/folders/${id}/items`, opts({ method: 'POST', body: JSON.stringify(body) }));
export const removeFolderItem = (id: number, itemId: number) =>
  apiRequest<void>(`${BASE}/folders/${id}/items/${itemId}`, opts({ method: 'DELETE' }));

export const listActivity = (limit = 30) => apiRequest<{ items: ActivityItem[] }>(`${BASE}/activity?limit=${limit}`, opts());
